/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { calcularLedgerComisionEdificio } from "../../utils/calcularLedgerComisionEdificio";
import { getDolarValue, getUFValue } from "../../utils/exchangeRateService";
import { getPagosEmpleados } from "../../utils/pagosEmpleados";
import { getSaldosIniciales, upsertSaldoInicial } from "../../utils/saldosIniciales";
import { client } from "../../utils/supabase";
import { useCurrency } from "../../utils/useCurrency";
import { Header } from "../Header";
import styles from "./EstadoCuenta.module.scss";

const FECHA_CORTE = "2026-01-01";

const SUB_TABS = [
  { value: "proveedor", label: "Proveedores" },
  { value: "cliente", label: "Clientes" },
  { value: "empleado", label: "Empleados" },
  { value: "edificio", label: "Edificios" },
];

const VISTAS_EDIFICIO = [
  { value: "mensual", label: "Mensual" },
  { value: "trimestral", label: "Trimestral" },
  { value: "anual", label: "Anual" },
];

const TIPO_PAGO_LABEL = {
  sueldo: "Sueldo",
  adelanto: "Adelanto",
  comision: "Comisión",
  bono: "Bono",
  finiquito: "Finiquito",
  transporte: "Transporte",
};

const formatFechaDisplay = (fechaStr) => {
  if (!fechaStr) return "-";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [year, month, day] = f.split("-");
  return `${day}/${month}/${year}`;
};

const getTrimestreFromDate = (fechaStr) => {
  if (!fechaStr) return "";
  const month = parseInt(fechaStr.substring(5, 7), 10);
  const year = fechaStr.substring(0, 4);
  const q = Math.ceil(month / 3);
  return `${year} Q${q}`;
};

const agruparPorTrimestre = (entries) => {
  const grouped = {};
  entries.forEach((e) => {
    const key = e.trimestre || getTrimestreFromDate(e.fecha);
    if (!key) return;
    if (!grouped[key]) {
      grouped[key] = {
        fecha: e.fecha,
        trimestre: key,
        concepto: key,
        referencia: "-",
        debito: 0,
        credito: 0,
        moneda: e.moneda,
      };
    }
    grouped[key].debito += e.debito;
    grouped[key].credito += e.credito;
  });
  return Object.values(grouped).sort((a, b) => a.fecha.localeCompare(b.fecha));
};

const agruparPorAno = (entries) => {
  const grouped = {};
  entries.forEach((e) => {
    const year = e.fecha ? e.fecha.substring(0, 4) : "?";
    if (!grouped[year]) {
      grouped[year] = {
        fecha: `${year}-12-31`,
        concepto: year,
        referencia: "-",
        debito: 0,
        credito: 0,
        moneda: e.moneda,
      };
    }
    grouped[year].debito += e.debito;
    grouped[year].credito += e.credito;
  });
  return Object.values(grouped).sort((a, b) => a.fecha.localeCompare(b.fecha));
};

const ORIGEN_CLASS = {
  SII: styles.origenSii,
  EXT: styles.origenExt,
  LEG: styles.origenLeg,
  INI: styles.origenIni,
};

const OrigenBadge = ({ origen, enteFiscal = "SII" }) => {
  if (!origen) return null;
  const label = origen === "SII" ? enteFiscal : origen;
  return <span className={`${styles.origenBadge} ${ORIGEN_CLASS[origen] || ""}`}>{label}</span>;
};

const EstadoCuenta = () => {
  const { pais } = useAuthContext();
  const { formatAmount } = useCurrency();

  const normalizedPais = pais ? pais.charAt(0).toUpperCase() + pais.slice(1).toLowerCase() : "";
  const monedaPrincipal = normalizedPais === "Chile" ? "CLP" : "PEN";
  const enteFiscal = normalizedPais === "Chile" ? "SII" : "SUNAT";

  const [subTab, setSubTab] = useState("proveedor");
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const [monedaSaldo, setMonedaSaldo] = useState(monedaPrincipal);
  const [vistaEdificio, setVistaEdificio] = useState("mensual");

  const [proveedores, setProveedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [movimientosCaja, setMovimientosCaja] = useState([]);
  const [siiRecibidas, setSiiRecibidas] = useState([]);
  const [siiEmitidas, setSiiEmitidas] = useState([]);
  const [facturasExternas, setFacturasExternas] = useState([]);
  const [facturasProveedor, setFacturasProveedor] = useState([]);
  const [pagosEmpleados, setPagosEmpleados] = useState([]);
  const [comisionesEdificio, setComisionesEdificio] = useState([]);
  const [loadingComisiones, setLoadingComisiones] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saldosIniciales, setSaldosIniciales] = useState([]);
  const [editandoSaldo, setEditandoSaldo] = useState(false);

  // ─── Carga inicial de datos ───
  useEffect(() => {
    if (!normalizedPais) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      client.from("proveedores").select("id, nombre").eq("pais", normalizedPais).order("nombre"),
      client.from("clientes").select("id, nombre, empresa").eq("pais", normalizedPais),
      client
        .from("empleados")
        .select("id, nombre, apellido, pais, egreso_fecha")
        .eq("pais", normalizedPais)
        .order("nombre"),
      client.from("edificios").select("id, edificio, alianza, pais").eq("pais", normalizedPais),
      client
        .from("movimientos_caja")
        .select(
          "id, fecha, monto, moneda, tipo, entidad_tipo, entidad_id, descripcion, numero_documento, categoria_pago, estado"
        )
        .eq("pais", normalizedPais)
        .neq("estado", "anulado")
        .gte("fecha", FECHA_CORTE)
        .order("fecha", { ascending: false }),
      client
        .from("facturas_sii_recibidas")
        .select("id, proveedor_id, fecha_emision, monto_total, folio, tipo_dte")
        .eq("pais", normalizedPais)
        .gte("fecha_emision", FECHA_CORTE),
      client
        .from("facturas_sii_emitidas")
        .select("id, cliente_id, fecha_emision, monto_total, folio, tipo_dte")
        .eq("pais", normalizedPais)
        .gte("fecha_emision", FECHA_CORTE),
      client
        .from("facturas_externas")
        .select("id, proveedor_id, fecha_emision, monto_total, moneda, numero_documento, estado")
        .eq("pais", normalizedPais)
        .gte("fecha_emision", FECHA_CORTE),
      client
        .from("facturas_proveedor")
        .select("id, proveedor_id, fecha_emision, monto_total, moneda, numero_factura, estado")
        .eq("pais", normalizedPais)
        .gte("fecha_emision", FECHA_CORTE),
      getPagosEmpleados({ pais: normalizedPais, desde: FECHA_CORTE }),
      getSaldosIniciales(normalizedPais),
    ])
      .then(([provs, clis, emps, edifs, movs, sR, sE, fE, fP, pE, sI]) => {
        if (cancelled) return;
        setProveedores(provs.data || []);
        setClientes(clis.data || []);
        setEmpleados(emps.data || []);
        setEdificios(edifs.data || []);
        setMovimientosCaja(movs.data || []);
        setSiiRecibidas(sR.data || []);
        setSiiEmitidas(sE.data || []);
        setFacturasExternas(fE.data || []);
        setFacturasProveedor(fP.data || []);
        setPagosEmpleados(pE || []);
        setSaldosIniciales(sI || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error cargando estado de cuenta:", err);
        toast.error("Error cargando estado de cuenta");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [normalizedPais]);

  // ─── Auto-detectar moneda predominante al elegir proveedor ───
  useEffect(() => {
    if (subTab !== "proveedor" || !selectedEntityId) return;
    const provId = Number(selectedEntityId);
    const fxs = facturasExternas.filter((f) => f.proveedor_id === provId && f.estado !== "anulada");
    const fps = facturasProveedor.filter((f) => f.proveedor_id === provId && f.estado !== "anulada");
    const total = fxs.length + fps.length;
    if (total === 0) {
      setMonedaSaldo(monedaPrincipal);
      return;
    }
    const usdCount = [...fxs, ...fps].filter((f) => f.moneda === "USD").length;
    setMonedaSaldo(usdCount > total / 2 ? "USD" : monedaPrincipal);
  }, [selectedEntityId, subTab, facturasExternas, facturasProveedor, monedaPrincipal]);

  // ─── Resolver IDs de edificios según selección ───
  const resolverEdificioIds = useCallback(
    (entityId) => {
      if (!entityId) return [];
      if (entityId === "todos") return edificios.map((e) => e.id);
      if (entityId.startsWith("alianza:")) {
        const alianza = entityId.replace("alianza:", "");
        return edificios.filter((e) => e.alianza === alianza).map((e) => e.id);
      }
      return [Number(entityId)];
    },
    [edificios]
  );

  // ─── Lazy-load comisiones edificio ───
  useEffect(() => {
    if (subTab !== "edificio" || !selectedEntityId) {
      setComisionesEdificio([]);
      return;
    }
    let cancelled = false;
    const fetchComisiones = async () => {
      setLoadingComisiones(true);
      try {
        const ids = resolverEdificioIds(selectedEntityId);
        if (ids.length === 0) {
          if (!cancelled) setComisionesEdificio([]);
          return;
        }
        const edificiosMap = {};
        edificios.forEach((e) => {
          edificiosMap[e.id] = e;
        });
        const result = await calcularLedgerComisionEdificio({
          edificioIds: ids,
          edificiosMap,
          fechaInicio: FECHA_CORTE,
          fechaFin: `${new Date().getFullYear() + 1}-12-31`,
          pais: normalizedPais,
        });
        if (!cancelled) setComisionesEdificio(result);
      } catch (err) {
        console.error("Error calculando comisiones edificio:", err);
        if (!cancelled) setComisionesEdificio([]);
      } finally {
        if (!cancelled) setLoadingComisiones(false);
      }
    };
    fetchComisiones();
    return () => {
      cancelled = true;
    };
  }, [subTab, selectedEntityId, edificios, normalizedPais, resolverEdificioIds]);

  // ─── Helpers de conversión moneda ───
  const toMonedaLocal = useCallback(
    (monto, moneda, fecha) => {
      if (!monto) return 0;
      if (moneda === "USD") return Math.round(monto * getDolarValue(fecha, normalizedPais));
      if (moneda === "UF") return Math.round(monto * getUFValue(fecha));
      return monto;
    },
    [normalizedPais]
  );

  const toDisplayCurrency = useCallback(
    (monto, monedaOrigen, fecha) => {
      if (!monto) return 0;
      const origen = monedaOrigen || monedaPrincipal;
      if (origen === monedaSaldo) return monto;
      if (monedaSaldo === monedaPrincipal) return toMonedaLocal(monto, origen, fecha);
      if (monedaSaldo === "USD") {
        if (origen === "USD") return monto;
        const rate = getDolarValue(fecha, normalizedPais);
        return rate > 0 ? Math.round(monto / rate) : 0;
      }
      return monto;
    },
    [monedaSaldo, monedaPrincipal, toMonedaLocal, normalizedPais]
  );

  // ─── Opciones del dropdown ───
  const uniqueAlianzas = useMemo(
    () => [...new Set(edificios.map((e) => e.alianza).filter(Boolean))].sort(),
    [edificios]
  );

  const entityOptions = useMemo(() => {
    if (subTab === "proveedor") return proveedores.map((p) => ({ value: p.id, label: p.nombre }));
    if (subTab === "cliente") {
      return [...clientes]
        .sort((a, b) => (a.empresa || a.nombre || "").localeCompare(b.empresa || b.nombre || ""))
        .map((c) => ({ value: c.id, label: c.empresa || c.nombre }));
    }
    if (subTab === "empleado") {
      return empleados.filter((e) => !e.egreso_fecha).map((e) => ({ value: e.id, label: `${e.nombre} ${e.apellido}` }));
    }
    return [];
  }, [subTab, proveedores, clientes, empleados]);

  const entityLabel =
    subTab === "proveedor"
      ? "Proveedor"
      : subTab === "cliente"
        ? "Cliente"
        : subTab === "empleado"
          ? "Empleado"
          : "Edificio";

  // ─── Saldo inicial de la entidad activa (si aplica) ───
  // Solo se busca para entidades simples (no para "todos" ni alianzas en edificios).
  const saldoInicialActivo = useMemo(() => {
    if (!selectedEntityId) return null;
    const id = Number(selectedEntityId);
    if (!Number.isFinite(id)) return null;
    return saldosIniciales.find((s) => s.entidad_tipo === subTab && s.entidad_id === id) || null;
  }, [saldosIniciales, subTab, selectedEntityId]);

  const buildSaldoInicialEntry = useCallback(
    (saldo) => {
      const monto = Number(saldo.monto || 0);
      return {
        fecha: FECHA_CORTE,
        concepto: "Saldo inicial al 01/01/2026",
        origen: "INI",
        referencia: saldo.notas || "",
        debito: monto > 0 ? monto : 0,
        credito: monto < 0 ? -monto : 0,
        moneda: saldo.moneda || monedaPrincipal,
      };
    },
    [monedaPrincipal]
  );

  // ─── LEDGER: PROVEEDORES (3 fuentes + pagos) ───
  const ledgerProveedor = useMemo(() => {
    if (subTab !== "proveedor" || !selectedEntityId) return [];
    const provId = Number(selectedEntityId);

    const debitosSii = siiRecibidas
      .filter((f) => f.proveedor_id === provId)
      .map((f) => ({
        fecha: f.fecha_emision,
        concepto: f.tipo_dte === 61 ? "NC recibida" : "Factura",
        origen: "SII",
        referencia: f.folio ? String(f.folio) : "-",
        debito: f.tipo_dte === 61 ? -Number(f.monto_total) : Number(f.monto_total),
        credito: 0,
        moneda: monedaPrincipal,
      }));

    const debitosExt = facturasExternas
      .filter((f) => f.proveedor_id === provId && f.estado !== "anulada")
      .map((f) => ({
        fecha: f.fecha_emision,
        concepto: "Factura externa",
        origen: "EXT",
        referencia: f.numero_documento || "-",
        debito: Number(f.monto_total),
        credito: 0,
        moneda: f.moneda || monedaPrincipal,
      }));

    const debitosLeg = facturasProveedor
      .filter((f) => f.proveedor_id === provId && f.estado !== "anulada")
      .map((f) => ({
        fecha: f.fecha_emision,
        concepto: "Factura legacy",
        origen: "LEG",
        referencia: f.numero_factura || "-",
        debito: Number(f.monto_total),
        credito: 0,
        moneda: f.moneda || monedaPrincipal,
      }));

    const creditos = movimientosCaja
      .filter((m) => m.tipo === "pago" && m.entidad_tipo === "proveedor" && m.entidad_id === provId)
      .map((m) => ({
        fecha: m.fecha,
        concepto: "Pago",
        origen: null,
        referencia: m.numero_documento || "-",
        debito: 0,
        credito: Number(m.monto),
        moneda: m.moneda || monedaPrincipal,
      }));

    const inicial = saldoInicialActivo ? [buildSaldoInicialEntry(saldoInicialActivo)] : [];
    const entries = [...inicial, ...debitosSii, ...debitosExt, ...debitosLeg, ...creditos].sort((a, b) =>
      a.fecha.localeCompare(b.fecha)
    );

    let saldo = 0;
    return entries
      .map((entry) => {
        const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
        const creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
        saldo += debitoDisplay - creditoDisplay;
        return { ...entry, debitoDisplay, creditoDisplay, saldo };
      })
      .reverse();
  }, [
    subTab,
    selectedEntityId,
    siiRecibidas,
    facturasExternas,
    facturasProveedor,
    movimientosCaja,
    monedaPrincipal,
    toDisplayCurrency,
    saldoInicialActivo,
    buildSaldoInicialEntry,
  ]);

  // ─── LEDGER: CLIENTES (SII emitidas + cobros) ───
  const ledgerCliente = useMemo(() => {
    if (subTab !== "cliente" || !selectedEntityId) return [];
    const cliId = Number(selectedEntityId);

    const debitos = siiEmitidas
      .filter((f) => f.cliente_id === cliId)
      .map((f) => ({
        fecha: f.fecha_emision,
        concepto: f.tipo_dte === 61 ? "NC emitida" : "Factura",
        origen: "SII",
        referencia: f.folio ? String(f.folio) : "-",
        debito: f.tipo_dte === 61 ? -Number(f.monto_total) : Number(f.monto_total),
        credito: 0,
        moneda: monedaPrincipal,
      }));

    const creditos = movimientosCaja
      .filter((m) => m.tipo === "cobro" && m.entidad_tipo === "cliente" && m.entidad_id === cliId)
      .map((m) => ({
        fecha: m.fecha,
        concepto: "Cobro",
        origen: null,
        referencia: m.numero_documento || "-",
        debito: 0,
        credito: Number(m.monto),
        moneda: m.moneda || monedaPrincipal,
      }));

    const inicial = saldoInicialActivo ? [buildSaldoInicialEntry(saldoInicialActivo)] : [];
    const entries = [...inicial, ...debitos, ...creditos].sort((a, b) => a.fecha.localeCompare(b.fecha));

    let saldo = 0;
    return entries
      .map((entry) => {
        const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
        const creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
        saldo += debitoDisplay - creditoDisplay;
        return { ...entry, debitoDisplay, creditoDisplay, saldo };
      })
      .reverse();
  }, [
    subTab,
    selectedEntityId,
    siiEmitidas,
    movimientosCaja,
    monedaPrincipal,
    toDisplayCurrency,
    saldoInicialActivo,
    buildSaldoInicialEntry,
  ]);

  // ─── LEDGER: EMPLEADOS (pagos_empleados) ───
  const ledgerEmpleado = useMemo(() => {
    if (subTab !== "empleado" || !selectedEntityId) return [];
    const empId = Number(selectedEntityId);

    const pagos = pagosEmpleados
      .filter((p) => p.empleado_id === empId && (p.fecha_pago || "") >= FECHA_CORTE)
      .map((p) => ({
        fecha: p.fecha_pago,
        concepto: TIPO_PAGO_LABEL[p.tipo] || p.tipo || "Pago",
        referencia: p.periodo_año && p.periodo_mes ? `${String(p.periodo_mes).padStart(2, "0")}/${p.periodo_año}` : "-",
        debito: 0,
        credito: Number(p.monto || 0),
        moneda: p.moneda || monedaPrincipal,
      }));

    const inicial = saldoInicialActivo ? [buildSaldoInicialEntry(saldoInicialActivo)] : [];
    const entries = [...inicial, ...pagos].sort((a, b) => a.fecha.localeCompare(b.fecha));

    let saldo = 0;
    return entries
      .map((entry) => {
        const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
        const creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
        saldo += debitoDisplay - creditoDisplay;
        return { ...entry, debitoDisplay, creditoDisplay, saldo };
      })
      .reverse();
  }, [
    subTab,
    selectedEntityId,
    pagosEmpleados,
    monedaPrincipal,
    toDisplayCurrency,
    saldoInicialActivo,
    buildSaldoInicialEntry,
  ]);

  // ─── LEDGER: EDIFICIOS (comisiones + pagos) ───
  const ledgerEdificio = useMemo(() => {
    if (subTab !== "edificio" || !selectedEntityId) return [];

    const targetIds = new Set(resolverEdificioIds(selectedEntityId));

    const debitos = comisionesEdificio.map((c) => ({
      fecha: c.fecha,
      trimestre: c.trimestre,
      concepto: c.concepto,
      referencia: "-",
      debito: c.debito,
      credito: 0,
      moneda: c.moneda,
    }));

    const creditos = movimientosCaja
      .filter((m) => m.tipo === "pago" && m.entidad_tipo === "edificio" && targetIds.has(m.entidad_id))
      .map((m) => ({
        fecha: m.fecha,
        trimestre: getTrimestreFromDate(m.fecha),
        concepto: "Pago",
        referencia: m.numero_documento || "-",
        debito: 0,
        credito: Number(m.monto),
        moneda: m.moneda || monedaPrincipal,
      }));

    // Saldo inicial solo aplica cuando es un edificio individual (no "todos" ni alianza)
    const inicialEntry = saldoInicialActivo
      ? [
          {
            ...buildSaldoInicialEntry(saldoInicialActivo),
            trimestre: getTrimestreFromDate(FECHA_CORTE),
          },
        ]
      : [];
    let entries = [...inicialEntry, ...debitos, ...creditos].sort((a, b) => a.fecha.localeCompare(b.fecha));

    if (vistaEdificio === "trimestral") entries = agruparPorTrimestre(entries);
    else if (vistaEdificio === "anual") entries = agruparPorAno(entries);

    let saldo = 0;
    return entries
      .map((entry) => {
        const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
        const creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
        saldo += debitoDisplay - creditoDisplay;
        return { ...entry, debitoDisplay, creditoDisplay, saldo };
      })
      .reverse();
  }, [
    subTab,
    selectedEntityId,
    comisionesEdificio,
    movimientosCaja,
    vistaEdificio,
    monedaPrincipal,
    toDisplayCurrency,
    resolverEdificioIds,
    saldoInicialActivo,
    buildSaldoInicialEntry,
  ]);

  const ledger =
    subTab === "proveedor"
      ? ledgerProveedor
      : subTab === "cliente"
        ? ledgerCliente
        : subTab === "empleado"
          ? ledgerEmpleado
          : ledgerEdificio;

  const totalDebitos = ledger.reduce((s, e) => s + e.debitoDisplay, 0);
  const totalCreditos = ledger.reduce((s, e) => s + e.creditoDisplay, 0);
  const saldoFinal = totalDebitos - totalCreditos;

  // El botón "Editar saldo inicial" solo tiene sentido para entidades simples (no agregaciones).
  const puedeEditarSaldoInicial = !!selectedEntityId && Number.isFinite(Number(selectedEntityId));

  const handleGuardarSaldoInicial = async ({ monto, moneda, notas }) => {
    if (!puedeEditarSaldoInicial) return;
    try {
      const saved = await upsertSaldoInicial({
        pais: normalizedPais,
        entidad_tipo: subTab,
        entidad_id: Number(selectedEntityId),
        monto,
        moneda,
        notas,
      });
      setSaldosIniciales((prev) => {
        const idx = prev.findIndex((s) => s.id === saved.id);
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = saved;
          return copy;
        }
        return [...prev, saved];
      });
      toast.success("Saldo inicial guardado");
      setEditandoSaldo(false);
    } catch (err) {
      console.error("Error guardando saldo inicial:", err);
      toast.error("Error guardando saldo inicial");
    }
  };

  // ─── RENDER ───
  return (
    <div className={styles.wrapper}>
      <Header title="Estado de Cuenta" />
      <div className={styles.content}>
        {loading ? (
          <div className={styles.empty}>Cargando…</div>
        ) : (
          <>
            <div className={styles.subTabs}>
              {SUB_TABS.map((st) => (
                <button
                  key={st.value}
                  className={`${styles.subTab} ${subTab === st.value ? styles.activeSubTab : ""}`}
                  onClick={() => {
                    setSubTab(st.value);
                    setSelectedEntityId("");
                    setMonedaSaldo(monedaPrincipal);
                  }}
                >
                  {st.label}
                </button>
              ))}
            </div>

            {subTab === "edificio" ? (
              <div className={styles.edificioControls}>
                <select
                  className={`form-select ${styles.entitySelect}`}
                  value={selectedEntityId}
                  onChange={(e) => setSelectedEntityId(e.target.value)}
                >
                  <option value="">Seleccionar edificio...</option>
                  {edificios.length > 1 && <option value="todos">Todos</option>}
                  {uniqueAlianzas.length > 0 && (
                    <optgroup label="Alianzas">
                      {uniqueAlianzas.map((a) => (
                        <option key={`alianza:${a}`} value={`alianza:${a}`}>
                          {a}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="Edificios">
                    {[...edificios]
                      .sort((a, b) => (a.edificio || "").localeCompare(b.edificio || ""))
                      .map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.edificio}
                        </option>
                      ))}
                  </optgroup>
                </select>

                {selectedEntityId && (
                  <div className={styles.vistaToggle}>
                    {VISTAS_EDIFICIO.map((v) => (
                      <button
                        key={v.value}
                        className={`${styles.vistaOption} ${vistaEdificio === v.value ? styles.vistaActiva : ""}`}
                        onClick={() => setVistaEdificio(v.value)}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                )}

                {selectedEntityId && !loadingComisiones && ledger.length > 0 && (
                  <SaldoResumen
                    totalDebitos={totalDebitos}
                    totalCreditos={totalCreditos}
                    saldoFinal={saldoFinal}
                    monedaSaldo={monedaSaldo}
                    formatAmount={formatAmount}
                  />
                )}

                {puedeEditarSaldoInicial && (
                  <button type="button" className={styles.btnSaldoInicial} onClick={() => setEditandoSaldo(true)}>
                    {saldoInicialActivo ? "Editar saldo inicial" : "+ Saldo inicial"}
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.estadoCuentaRow}>
                <select
                  className={`form-select ${styles.entitySelect}`}
                  value={selectedEntityId}
                  onChange={(e) => setSelectedEntityId(e.target.value)}
                >
                  <option value="">Seleccionar {entityLabel.toLowerCase()}...</option>
                  {entityOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>

                {selectedEntityId && (
                  <select
                    className={`form-select ${styles.monedaSelect}`}
                    value={monedaSaldo}
                    onChange={(e) => setMonedaSaldo(e.target.value)}
                  >
                    <option value={monedaPrincipal}>{monedaPrincipal}</option>
                    <option value="USD">USD</option>
                  </select>
                )}

                {selectedEntityId && ledger.length > 0 && (
                  <SaldoResumen
                    totalDebitos={totalDebitos}
                    totalCreditos={totalCreditos}
                    saldoFinal={saldoFinal}
                    monedaSaldo={monedaSaldo}
                    formatAmount={formatAmount}
                  />
                )}

                {puedeEditarSaldoInicial && (
                  <button type="button" className={styles.btnSaldoInicial} onClick={() => setEditandoSaldo(true)}>
                    {saldoInicialActivo ? "Editar saldo inicial" : "+ Saldo inicial"}
                  </button>
                )}
              </div>
            )}

            {!selectedEntityId && (
              <div className={styles.empty}>Seleccione una entidad para ver su estado de cuenta</div>
            )}
            {selectedEntityId && loadingComisiones && subTab === "edificio" && (
              <div className={styles.empty}>Calculando comisiones...</div>
            )}
            {selectedEntityId && !loadingComisiones && ledger.length === 0 && (
              <div className={styles.empty}>Sin movimientos registrados desde 2026-01-01</div>
            )}

            {selectedEntityId && !loadingComisiones && ledger.length > 0 && (
              <div className={styles.tableWrapper}>
                <LedgerTable
                  subTab={subTab}
                  ledger={ledger}
                  monedaSaldo={monedaSaldo}
                  formatAmount={formatAmount}
                  enteFiscal={enteFiscal}
                />
              </div>
            )}
          </>
        )}
      </div>

      {editandoSaldo && puedeEditarSaldoInicial && (
        <SaldoInicialModal
          tipoLabel={entityLabel}
          saldoActual={saldoInicialActivo}
          monedaPrincipal={monedaPrincipal}
          onClose={() => setEditandoSaldo(false)}
          onGuardar={handleGuardarSaldoInicial}
        />
      )}
    </div>
  );
};

const SaldoResumen = ({ totalDebitos, totalCreditos, saldoFinal, monedaSaldo, formatAmount }) => (
  <div className={styles.saldoResumen}>
    <div className={styles.saldoItem}>
      <span className={styles.saldoItemLabel}>Debe</span>
      <span className={`${styles.saldoItemValue} ${styles.saldoDeuda}`}>{formatAmount(totalDebitos, monedaSaldo)}</span>
    </div>
    <div className={styles.saldoItem}>
      <span className={styles.saldoItemLabel}>Haber</span>
      <span className={`${styles.saldoItemValue} ${styles.saldoFavor}`}>
        {formatAmount(totalCreditos, monedaSaldo)}
      </span>
    </div>
    <div className={styles.saldoItem}>
      <span className={styles.saldoItemLabel}>Saldo</span>
      <span
        className={`${styles.saldoItemValue} ${
          saldoFinal > 0 ? styles.saldoDeuda : saldoFinal < 0 ? styles.saldoFavor : ""
        }`}
      >
        {formatAmount(Math.abs(saldoFinal), monedaSaldo)}
      </span>
    </div>
  </div>
);

const LedgerTable = ({ subTab, ledger, monedaSaldo, formatAmount, enteFiscal = "SII" }) => {
  const isEdificio = subTab === "edificio";
  const isEmpleado = subTab === "empleado";
  const tableClass = `${styles.estadoCuentaTable} ${
    isEdificio ? styles.estadoCuentaEdificio : isEmpleado ? styles.estadoCuentaEmpleado : ""
  }`;

  return (
    <table className={tableClass}>
      <thead>
        <tr>
          {isEdificio ? (
            <th>Período</th>
          ) : isEmpleado ? (
            <>
              <th>Fecha</th>
              <th>Concepto</th>
              <th>Referencia</th>
            </>
          ) : (
            <>
              <th>Fecha</th>
              <th>Concepto</th>
              <th>Origen</th>
              <th>Referencia</th>
            </>
          )}
          <th>Debe</th>
          <th>Haber</th>
          <th>Saldo</th>
        </tr>
      </thead>
      <tbody>
        {ledger.map((entry, idx) => (
          <tr key={idx}>
            {isEdificio ? (
              <td>{entry.concepto}</td>
            ) : isEmpleado ? (
              <>
                <td>{formatFechaDisplay(entry.fecha)}</td>
                <td>{entry.concepto}</td>
                <td>{entry.referencia}</td>
              </>
            ) : (
              <>
                <td>{formatFechaDisplay(entry.fecha)}</td>
                <td>{entry.concepto}</td>
                <td>
                  <OrigenBadge origen={entry.origen} enteFiscal={enteFiscal} />
                </td>
                <td>{entry.referencia}</td>
              </>
            )}
            <td style={{ color: entry.debitoDisplay > 0 ? "#c62828" : undefined }}>
              {entry.debitoDisplay > 0 ? formatAmount(entry.debitoDisplay, monedaSaldo) : ""}
            </td>
            <td style={{ color: entry.creditoDisplay > 0 ? "#2e7d32" : undefined }}>
              {entry.creditoDisplay > 0 ? formatAmount(entry.creditoDisplay, monedaSaldo) : ""}
            </td>
            <td
              style={{
                fontWeight: 600,
                color: entry.saldo > 0 ? "#c62828" : entry.saldo < 0 ? "#2e7d32" : "#333",
              }}
            >
              {formatAmount(Math.abs(entry.saldo), monedaSaldo)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const SaldoInicialModal = ({ tipoLabel, saldoActual, monedaPrincipal, onClose, onGuardar }) => {
  const [monto, setMonto] = useState(saldoActual ? String(saldoActual.monto) : "");
  const [moneda, setMoneda] = useState(saldoActual?.moneda || monedaPrincipal);
  const [notas, setNotas] = useState(saldoActual?.notas || "");
  const [guardando, setGuardando] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const montoNum = Number(monto);
    if (Number.isNaN(montoNum)) {
      toast.error("Monto inválido");
      return;
    }
    setGuardando(true);
    try {
      await onGuardar({ monto: montoNum, moneda, notas: notas.trim() });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{
          background: "#fff",
          borderRadius: 8,
          width: "min(440px, 95vw)",
          padding: 20,
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h5 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#1a3a5c" }}>
            Saldo inicial al 01/01/2026 — {tipoLabel}
          </h5>
          <button
            type="button"
            onClick={onClose}
            style={{ border: "none", background: "transparent", fontSize: "1.4rem", cursor: "pointer", color: "#666" }}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div style={{ fontSize: "0.78rem", color: "#666", marginBottom: 12 }}>
          Positivo = la entidad nos debe. Negativo = le debemos a la entidad.
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: "0.78rem", color: "#555", marginBottom: 4 }}>Monto</label>
          <input
            type="number"
            step="any"
            className="form-control form-control-sm"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            autoFocus
            required
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: "0.78rem", color: "#555", marginBottom: 4 }}>Moneda</label>
          <select className="form-select form-select-sm" value={moneda} onChange={(e) => setMoneda(e.target.value)}>
            <option value={monedaPrincipal}>{monedaPrincipal}</option>
            <option value="USD">USD</option>
            {monedaPrincipal !== "CLP" && <option value="CLP">CLP</option>}
            {monedaPrincipal !== "PEN" && <option value="PEN">PEN</option>}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: "0.78rem", color: "#555", marginBottom: 4 }}>
            Notas (opcional)
          </label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Ej: cierre 2025, pendiente de cobro Empresa X"
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose} disabled={guardando}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-sm btn-primary" disabled={guardando}>
            {guardando ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EstadoCuenta;
